import { Component, OnInit, Input, Output, EventEmitter, ViewChild, SimpleChanges, inject } from '@angular/core';
import { Hotel } from 'app/shared/model';
import { BaseChartDirective } from 'ng2-charts';
import { ChartEvent, ActiveElement } from 'chart.js';
import { TrendLegendData } from './trends-legend/trend-legend.model';
import {UtilService} from '../../../shared/util.service';
import { TrendsTableComponent } from './trends-table/trends-table.component';
import { Router } from '@angular/router';

@Component({
    selector: 'widget-trends',
    templateUrl: './trends.component.html',
    styleUrls: ['./trends.component.scss'],
    standalone: false
})
export class TrendsComponent implements OnInit {

  @Input()
  chain: Hotel[] = [];

  @Input()
  trendKeys = [];

  @Input()
  areasKeys = [];

  @Input()
  showDefaultLegends = false;

  @Input()
  waiting = true;

  @Input()
  lineChartData;

  @Input()
  lineChartLabels;

  @Input()
  lineChartColors;

  @Input()
  trendSelected;

  @Input()
  areaSelected;

  @Input()
  segmentedSelected;

  @Input()
  firstColumn = 'hotel';

  @Input()
  showDetail = false;

  @Input()
  totals = {}

  @Input()
  showFilters;

  @Input()
  prefixTranslateFilters = 'widgets.trends.filters';

  @Input()
  showOtherFilters = true;

  @Input()
  currentTrend;

  @Input()
  ignoredKeysOfTable = [];

  @Input()
  weightedAverageData: number[] = [];

  @Output()
  filterTypeChange: EventEmitter<any> = new EventEmitter();

  @Output()
  filterCustomerChange: EventEmitter<number[]> = new EventEmitter();

  @Output()
  detailSelected: EventEmitter<any> = new EventEmitter();

  @Output()
  chartClick: EventEmitter<any> = new EventEmitter();

  chartLegends='';
  isCorporative = false;
  trendWithsTotalsHidden = ['rate'];

  router = inject(Router);


  lists = {
    'trend' : [],
    'areas' : [],
    'segmentedBy' : [
      'months',
      'weeks',
      'days'
    ]
  };

  lineChartLegend:boolean = false;
  lineChartType: 'line' = 'line';
  chartData: any = { labels: [], datasets: [] };

  // Plugin local para datalabels encapsulado
  // Plugins locales se configuran directamente en lineChartPlugins
  public lineChartPlugins = [];

  lineChartOptions:any = {
    maintainAspectRatio: false,
    responsive: true,
    layout: {
      padding: {
        top: 30
      }
    },
    elements:{
      line: {
        tension: 0.5,
      },
      point: {
        radius: 2,
        hitRadius: 10,
        hoverRadius: 4,
      }
    },
    scales:{
      y: {
        ticks: {}
      }
    },
    interaction: {
      mode: 'point',
      intersect: true,
    },
    plugins: {
      legend: {
        display: false,
        onHover: function(e) {
          if (e?.native?.target?.style) {
            e.native.target.style.cursor = 'pointer';
          }
        }
      },
      tooltip: {
        mode: 'point',
        intersect: true,
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        titleColor: '#fff',
        bodyColor: '#fff',
        borderColor: 'rgba(0, 0, 0, 0.8)',
        borderWidth: 1,
        padding: 12,
        displayColors: true,
        boxWidth: 5,
        boxHeight: 5,
        usePointStyle: true,
      },
      datalabels: {
        display: false
      }
    }
  };

  @ViewChild(BaseChartDirective, { static: false })
  chart: BaseChartDirective | any;

  @ViewChild('tableTrend', { static: false })
  tableTrend: TrendsTableComponent;


  constructor(
    private utilService: UtilService
  ) { 
    this.createLocalDataLabelsPlugin();
  }

  createLocalDataLabelsPlugin() {
    // Labels config
    const localDataLabelsPlugin = {
      id: 'localDataLabels',
      afterDatasetsDraw: (chart: any) => {
        if (chart.data.datasets.length !== 1) return;
        
        const ctx = chart.ctx;
        chart.data.datasets.forEach((dataset: any, datasetIndex: number) => {
          if (dataset.hidden) return;
          
          const meta = chart.getDatasetMeta(datasetIndex);
          meta.data.forEach((element: any, index: number) => {
            if (element.hidden) return;
            
            const value = dataset.data[index];
            
            // No mostrar si el valor es null o undefined
            if (value === null || value === undefined) return;
            
            const component = this;
            let displayValue = value;
            
            // Aplicar el formatter personalizado
            if (component && component.currentTrend?.value === 'nps' && value > 0) {
              displayValue = `+${value}`;
            }
            
            // Configuración del texto
            ctx.font = 'bold 12px Arial';
            ctx.fillStyle = '#000';
            ctx.textAlign = 'center';
            ctx.textBaseline = 'bottom';
            
            const position = element.tooltipPosition();
            ctx.fillText(displayValue, position.x, position.y - 5);
          });
        });
      }
    };
    
    this.lineChartPlugins = [localDataLabelsPlugin];
  }

  ngOnInit() {
    this.lists.trend  = this.trendKeys;
    this.lineChartOptions.plugins.legend.display = this.showDefaultLegends;
    if(this.showDetail){
      this.lineChartOptions.onHover = function(event, activeElements, chart) {
        if (event?.native?.target?.style) {
          if (activeElements.length > 0) {
            event.native.target.style.cursor = 'pointer';
          } else {
            event.native.target.style.cursor = 'default';
          }
        }
      };
    }
    this.setAxes();
    this.isCorporative = this.router.url.includes('intercadena');
    this.updateLineChartDataTable();
    this.updateChartData();
  }

  ngOnDestroy(): void {
    this.lineChartPlugins = [];
  }

  setOnlyAverage() {
    let containAverage = false;
    this.lineChartData.forEach(data => {
      if(data.isAverage) {
        containAverage = true;
      }
    });
    if(this.trendSelected!=='ranking' && containAverage) {
      this.lineChartData.forEach(element => {
        if(!element.isAverage) {
          element.hidden = true;
        }
      });
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if(changes['areasKeys']) this.lists.areas = this.areasKeys;
    this.lists.trend  = this.trendKeys;
    
    if(changes['lineChartData'] || changes['ignoredKeysOfTable']) {
      this.updateLineChartDataTable();
    }

    // Actualizar chartData cuando cambien los datos o colores
    if(changes['lineChartData'] || changes['lineChartLabels'] || changes['lineChartColors']) {
      this.updateChartData();
    }
    
    if(!this.waiting) {
      this.setAxes();
    }
  }

  updateChartData() {
    if (!this.lineChartData || !this.lineChartLabels) {
      this.chartData = { labels: [], datasets: [] };
      return;
    }

    // Combinar lineChartData con lineChartColors
    const datasets = this.lineChartData.map((dataset: any, index: number) => {
      const colors = this.lineChartColors?.[index] || {};
      const borderColor = colors.borderColor || dataset.borderColor;
      const backgroundColor = colors.backgroundColor || dataset.backgroundColor;
      
      const result: any = {
        ...dataset,
        backgroundColor: backgroundColor,
        borderColor: borderColor,
        pointBackgroundColor: borderColor,
        pointBorderColor: borderColor,
        pointHoverBackgroundColor: borderColor,
        pointHoverBorderColor: borderColor,
        fill: colors.fill !== undefined ? colors.fill : (dataset.fill !== undefined ? dataset.fill : false),
      };
      
      // Si es el promedio, hacer la línea punteada
      if (dataset.isAverage) {
        result.borderDash = [5, 5];
      }
      
      return result;
    });

    this.chartData = {
      labels: this.lineChartLabels,
      datasets: datasets
    };
  }

  filterCustomers($event:TrendLegendData[]){
    if(this.chart) {
      this.chart.chart.data.datasets.forEach((element,index) => {
        element.hidden = $event[index].hidden;
      });
      this.chart.chart.update();
    }
  }

  setSelectlValue(option, key){
    if(key==='trend') this.trendSelected = option.value;
    if(key==='segmentedBy') this.segmentedSelected = option.value;
    this.filterTypeChange.emit({
      option: option,
      key: key
    });
    this.setAxes();
  }


  setAxes(){
    const self = this;
    const inPercent = ['ordersRate','emailRate','pms'].includes(self.trendSelected);
    const reverse   = ['rankingTripAdvisor','ranking'].includes(self.trendSelected);

    const properties = {
      reverse: reverse,
      autoSkip: true,
      autoSkipPadding: 10,
      callback: function (value, index, values) {
        if ('totalPrice' === self.trendSelected) return `$${value}`;
        if (inPercent) return `${value}%`;
        if (['rankingTripAdvisor', 'ranking'].includes(self.trendSelected)) return `#${value}`;
        return value;
      },
    };

    if(['rankingTripAdvisor','ranking'].includes(self.trendSelected)){
      properties["min"] = 1;
    }

    if(inPercent){
      properties["suggestedMin"] = 0;
      properties["suggestedMax"] = 100;
    }

    if(['nps'].includes(self.trendSelected)) {
      properties["max"] = 100;
      properties["min"] = this.setMinNpsGraphic();
    }

    // Actualizar tooltip callbacks para Chart.js v4
    this.lineChartOptions.plugins.tooltip.callbacks = {
      label: function(context) {
        const customer = context.dataset;
        let value = context.parsed.y;
        
        // No mostrar tooltip si el valor es null o undefined
        if (value === null || value === undefined) {
          return '';
        }
        
        let formattedValue = value;
        
        if(['totalPrice','ordersRate','emailRate'].includes(self.trendSelected)){
          formattedValue = 'totalPrice'===self.trendSelected ? `$${value}`: `${value}%`;
        }
        else if(['nps'].includes(self.trendSelected)){
          formattedValue = value > 0 ? `+${value}`: value;
        }
        else if(['rankingTripAdvisor','ranking'].includes(self.trendSelected)){
          formattedValue = `#${value}`;
        }
        else if(['pms'].includes(self.trendSelected)){
          formattedValue = `${value}%`;
        }
        else if(['note','ranking_areas'].includes(self.trendSelected)){
          formattedValue = self.utilService.formatDecimals(value);
        }

        return customer.label + ': ' + formattedValue;
      }
    };

    this.lineChartOptions.scales.y.ticks = properties;
  }

  setMinNpsGraphic() {
    let min = 0;
    this.lineChartData.forEach(customer => {
      customer.data.forEach(value => {
        min = Math.min(min, value);
      })
    });
    return min;
  }

  emitDetailSelected($event){
    this.detailSelected.emit($event);
  }

  chartClicked(e: { event?: ChartEvent; active?: ActiveElement[] }): void {
    if (e.active && e.active.length > 0 && this.showDetail) {
      const activeElement = e.active[0];
      const datasetIndex = activeElement.datasetIndex;
      const cell = this.lineChartData[datasetIndex];

      const result = {};
      result[this.firstColumn] = cell.label;

      cell.data.forEach((data, i) => {
        result[this.lineChartLabels[i]] = data;
      });

      this.tableTrend.openDialog(result);
    }
  }

  lineChartDataTable: any[] = [];

  private updateLineChartDataTable() {
    this.lineChartDataTable = this.lineChartData?.filter(x => !this.ignoredKeysOfTable?.includes(x.label)) || [];
  }

  get hideTotalRow(){
    return this.trendWithsTotalsHidden.includes(this.trendSelected);
  }
}
